/**
 * Per-row ⋮ menu for FACILITY tables — OR-1431.
 *
 * Replaces the bare trash button that used to sit in the sticky right-hand
 * "actions" column. The team needs to distinguish two intents that the single
 * trash icon collapsed into one:
 *
 *   - **Archive** — the underwriter reviewed the facility and decided not to
 *     move forward with it, but the data stays on the submission for a future
 *     underwriting cycle. Reversible.
 *   - **Delete** — a data-entry mistake (wrong facility added). Permanent.
 *
 * Once archived, the first item becomes **Restore**. Copy is verbatim from
 * Andressa's spec — the descriptions are the only warning the user gets, since
 * there is no confirmation modal in the flow.
 *
 * Used directly on tables whose rows ARE facilities (Schedule, Regulatory,
 * Operational), and via `RowDeleteMenu`'s `facilityArchive` slot on tables
 * whose rows reference a facility but aren't one themselves (Loss Runs,
 * Legal) — see the header of `row-actions-menu.tsx`.
 */
import { Folder, RefreshCcw01, Trash02 } from "@untitledui/icons";
import { MenuItem as AriaMenuItem, Separator as AriaSeparator } from "react-aria-components";
import {
    ROW_ACTIONS_ITEM_CLASS,
    ROW_ACTIONS_SEPARATOR_CLASS,
    RowActionsMenu,
    RowActionsMenuItemBody,
} from "./row-actions-menu";

export interface FacilityRowActionsMenuProps {
    /** Drives Archive vs Restore. */
    isArchived: boolean;
    /**
     * Omit to hide the Archive/Restore item entirely — e.g. Legal only ever
     * offers Restore, never Archive, so it passes no `onArchive`.
     */
    onArchive?: () => void;
    onRestore?: () => void;
    /**
     * Omit to hide the Delete item entirely — e.g. Legal has no delete concept
     * for a litigation row at all.
     */
    onDelete?: () => void;
    /**
     * Whether this row can be archived at all. Defaults to true.
     *
     * Archiving acts on the row's submission location, and on the Regulatory
     * tabs a row can exist without one — its occupancy/survey records belong to
     * a catalog facility that was never added to this submission's schedule.
     * Those rows render Delete only, rather than an Archive item that would
     * silently do nothing.
     */
    canArchive?: boolean;
    /** Used in the aria-labels so screen readers can tell rows apart. */
    facilityName?: string;
    /** Overrides the Delete item's copy — defaults to the facility-delete
     *  sentence below, which is only accurate on Schedule. */
    deleteTitle?: string;
    deleteDescription?: string;
    /**
     * Renders Delete as disabled with this explanatory text instead of
     * omitting it — for CMS-sourced Regulatory rows where the backend 403s a
     * delete of the underlying record, but facility Archive/Restore still
     * works fine (it's a different resource).
     */
    deleteDisabledHint?: string;
    /** Extra classes for the trigger button. */
    className?: string;
    /** Custom attribute for E2E testing. */
    "data-autotest"?: string;
}

/** Stable collection key for the Archive/Restore item — see the comment at the
 *  item itself. react-stately treats a changed id at the same position as an
 *  error, so this must NOT depend on `isArchived`. */
const TOGGLE_ITEM_ID = "toggle-archive";

export function FacilityRowActionsMenu({
    isArchived,
    onArchive,
    onRestore,
    onDelete,
    canArchive = true,
    facilityName,
    deleteTitle = "Delete",
    deleteDescription = "Delete this facility from the submission and all associated data. This action cannot be undone.",
    deleteDisabledHint,
    className,
    "data-autotest": dataAutotest,
}: FacilityRowActionsMenuProps) {
    const label = facilityName?.trim() ? `Actions for ${facilityName.trim()}` : "Row actions";
    // Only the handler for the CURRENT state has to be present — Legal, e.g.,
    // passes only `onRestore` (never `onArchive`), so an archived row there
    // still shows the item even though `onArchive` is undefined.
    const showArchive = canArchive && (isArchived ? onRestore !== undefined : onArchive !== undefined);
    const showDelete = onDelete !== undefined || deleteDisabledHint !== undefined;
    const deleteDisabled = onDelete === undefined;

    return (
        <RowActionsMenu
            label={label}
            className={className}
            data-autotest={dataAutotest}
            onAction={(key) => {
                if (key === TOGGLE_ITEM_ID) {
                    if (isArchived) onRestore?.();
                    else onArchive?.();
                } else if (key === "delete") onDelete?.();
            }}
        >
            {/* ONE item that swaps its label, not two items that swap places.
              * Giving Archive and Restore different ids threw "Cannot change the
              * id of an item" from react-stately: acting on the item flips
              * `isArchived` while the popover is still mounted, so the
              * collection saw the id at that position change under it. The id
              * stays put; only the copy, icon and the action behind it change. */}
            {showArchive && (
                <AriaMenuItem
                    id={TOGGLE_ITEM_ID}
                    className={ROW_ACTIONS_ITEM_CLASS}
                    textValue={isArchived ? "Restore" : "Archive"}
                    data-autotest={dataAutotest ? `${dataAutotest}-${isArchived ? "restore" : "archive"}` : undefined}
                >
                    {isArchived ? (
                        <RowActionsMenuItemBody
                            icon={RefreshCcw01}
                            title="Restore"
                            description="Add this facility back to the submission with its original data and history intact"
                        />
                    ) : (
                        <RowActionsMenuItemBody
                            icon={Folder}
                            title="Archive"
                            description="Remove this facility from the submission while preserving its history and data. It can be restored later."
                        />
                    )}
                </AriaMenuItem>
            )}
            {/* Rule between the two actions, per the Figma. Only earns its place
              * when there are two items to separate. */}
            {showArchive && showDelete && <AriaSeparator className={ROW_ACTIONS_SEPARATOR_CLASS} />}
            {showDelete && (
                <AriaMenuItem
                    id="delete"
                    className={ROW_ACTIONS_ITEM_CLASS}
                    textValue={deleteTitle}
                    isDisabled={deleteDisabled}
                    data-autotest={dataAutotest ? `${dataAutotest}-delete` : undefined}
                >
                    <RowActionsMenuItemBody
                        destructive={!deleteDisabled}
                        icon={Trash02}
                        title={deleteTitle}
                        description={deleteDisabled ? (deleteDisabledHint ?? deleteDescription) : deleteDescription}
                    />
                </AriaMenuItem>
            )}
        </RowActionsMenu>
    );
}
